import { Controller } from "@hotwired/stimulus"

// Select con búsqueda, sobre un input hidden que es el que viaja en el
// formulario. Reemplaza a select2 sin sumar dependencias.
//
// Las opciones se leen una sola vez desde un <script type="application/json">
// compartido por toda la página (data-combobox-source-value), así no se repiten
// cientos de <option> por cada fila.
//
// Los combobox que comparten `group` forman una lista sin repetidos: lo que ya
// eligió otro aparece marcado con su posición y no se puede volver a elegir.
export default class extends Controller {
  static targets = ["input", "hidden", "list", "empty"]
  static values = {
    source: String,
    group: String,
    placeholder: { type: String, default: "Buscar…" },
    maxResults: { type: Number, default: 50 }
  }

  connect() {
    this.options = this.#loadOptions()
    this.activeIndex = -1
    this.#renderSelection()

    this.closeOnOutsideClick = (event) => {
      if (!this.element.contains(event.target)) this.close()
    }
    document.addEventListener("click", this.closeOnOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.closeOnOutsideClick)
  }

  open() {
    this.#filter(this.inputTarget.value === this.#selectedLabel() ? "" : this.inputTarget.value)
    this.listTarget.classList.remove("hidden")
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  close() {
    this.listTarget.classList.add("hidden")
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.activeIndex = -1
    // Si se escribió algo sin elegir, se vuelve a mostrar lo seleccionado.
    this.#renderSelection()
  }

  search() {
    this.#filter(this.inputTarget.value)
    this.listTarget.classList.remove("hidden")
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  keydown(event) {
    const items = this.#items()

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        if (this.listTarget.classList.contains("hidden")) return this.open()
        this.#highlight(Math.min(this.activeIndex + 1, items.length - 1))
        break
      case "ArrowUp":
        event.preventDefault()
        this.#highlight(Math.max(this.activeIndex - 1, 0))
        break
      case "Enter":
        if (!this.listTarget.classList.contains("hidden")) {
          event.preventDefault()
          items[this.activeIndex]?.click()
        }
        break
      case "Escape":
        this.close()
        break
    }
  }

  pick(event) {
    const { value, label } = event.currentTarget.dataset
    this.hiddenTarget.value = value
    this.inputTarget.value = label
    this.close()
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  clear() {
    this.hiddenTarget.value = ""
    this.inputTarget.value = ""
    // Igual que pick(): quien escuche el hidden debe enterarse del cambio.
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.inputTarget.focus()
    this.open()
  }

  #loadOptions() {
    const source = document.getElementById(this.sourceValue)
    return source ? JSON.parse(source.textContent) : []
  }

  // Sin acentos y en minúsculas, para que "cancion" encuentre "canción".
  #normalize(text) {
    return text.toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
  }

  // Los demás combobox del grupo, en el orden en que están en la página: ese
  // orden es la posición que se muestra al marcar una opción como ocupada.
  #taken() {
    const taken = new Map()
    if (!this.groupValue) return taken

    document.querySelectorAll(`[data-combobox-group-value="${this.groupValue}"]`)
      .forEach((peer, index) => {
        if (peer === this.element) return

        const value = peer.querySelector("[data-combobox-target='hidden']")?.value
        if (value) taken.set(value, index + 1)
      })
    return taken
  }

  #filter(query) {
    const terms = this.#normalize(query).split(/\s+/).filter(Boolean)
    const taken = this.#taken()
    const matches = this.options
      .filter((option) => terms.every((term) => this.#normalize(option.search || option.label).includes(term)))
      .slice(0, this.maxResultsValue)

    this.listTarget.innerHTML = matches.map((option, index) => {
      const position = taken.get(String(option.value))

      return `
        <li role="option" id="${this.element.id}-opt-${index}"
            data-combobox-target="item" data-value="${option.value}"
            data-label="${this.#escape(option.label)}"
            ${position ? 'aria-disabled="true"' : 'data-action="click->combobox#pick"'}
            class="flex items-center gap-3 px-4 py-2.5 font-sans text-[14px]
                   ${position ? "cursor-not-allowed" : "cursor-pointer hover:bg-pale"}">
          <span class="min-w-0 flex-1">
            <span class="block font-semibold ${position ? "text-faint" : "text-navy"}">${this.#escape(option.label)}</span>
            ${option.hint ? `<span class="block text-[12px] ${position ? "text-faint" : "text-dim"}">${this.#escape(option.hint)}</span>` : ""}
          </span>
          ${position ? `<span title="Ya está en la posición ${position}"
                              class="flex size-6 flex-none items-center justify-center rounded-full
                                     bg-gold/15 font-sans text-[12px] font-black text-gold-dark">${position}</span>` : ""}
        </li>
      `
    }).join("")

    if (matches.length === 0) {
      this.listTarget.innerHTML = `<li class="px-4 py-3 font-sans text-[14px] text-dim">Sin coincidencias</li>`
    }
    this.activeIndex = -1
  }

  // Solo las elegibles: así las flechas y Enter saltan las que ya están usadas.
  #items() {
    return Array.from(
      this.listTarget.querySelectorAll("[data-combobox-target='item']:not([aria-disabled='true'])")
    )
  }

  #highlight(index) {
    const items = this.#items()
    if (!items.length) return

    this.activeIndex = index
    items.forEach((item, i) => item.classList.toggle("bg-pale", i === index))
    items[index]?.scrollIntoView({ block: "nearest" })
    this.inputTarget.setAttribute("aria-activedescendant", items[index]?.id || "")
  }

  #selectedLabel() {
    const selected = this.options.find((option) => String(option.value) === this.hiddenTarget.value)
    return selected ? selected.label : ""
  }

  #renderSelection() {
    this.inputTarget.value = this.#selectedLabel()
  }

  #escape(text) {
    const element = document.createElement("div")
    element.textContent = text
    return element.innerHTML.replace(/"/g, "&quot;")
  }
}
